import React, { useState, useEffect } from 'react';
import { CreditCard, HelpCircle, X } from 'lucide-react';

const ModalMetodosCobro = ({ isOpen, onClose, onMetodoCreado }) => {
  const [tipoMetodo, setTipoMetodo] = useState('Efectivo');
  const [nombreMetodo, setNombreMetodo] = useState('');
  const [numeroCuenta, setNumeroCuenta] = useState('');
  const [cuentaVinculada, setCuentaVinculada] = useState('Nueva cuenta'); // Asumir una opción por defecto
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      // Resetear formulario al abrir
      setTipoMetodo('Efectivo');
      setNombreMetodo('');
      setNumeroCuenta('');
      setCuentaVinculada('Nueva cuenta');
      setError(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    // Lógica para guardar el nuevo método de cobro
    // Aquí deberías llamar a tu API para registrar el método
    console.log('Nuevo Método de Cobro:', { tipoMetodo, nombreMetodo, numeroCuenta, cuentaVinculada });
    alert('Lógica para crear método de cobro no implementada aún. Ver consola.');

    setLoading(false);
    if (onMetodoCreado) {
      onMetodoCreado({ tipoMetodo, nombreMetodo, numeroCuenta, cuentaVinculada });
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-11/12 max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <CreditCard size={20} className="text-blue-600" />
            Crear un nuevo método de cobro
          </h2>
          <button onClick={onClose} className="bg-none border-none text-gray-600 text-2xl p-1 rounded hover:bg-gray-200">
            <X size={24} />
          </button>
        </div>
        
        {/* Modal Body */}
        <div className="p-5">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-[200px_1fr] gap-4 items-center mb-4">
              <label htmlFor="tipoMetodo" className="font-medium text-right text-gray-700">Tipo de método</label>
              <select 
                id="tipoMetodo" 
                value={tipoMetodo}
                onChange={(e) => setTipoMetodo(e.target.value)}
                className="p-2 border border-gray-300 rounded-md text-sm bg-white max-w-[200px] focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              >
                <option>Efectivo</option>
                <option>Transferencia</option>
                <option>Tarjeta de Crédito</option>
                <option>Cheque</option>
                {/* Más opciones */}
              </select>
            </div>

            <div className="grid grid-cols-[200px_1fr] gap-4 items-center mb-4">
              <label htmlFor="nombreMetodo" className="font-medium text-right text-gray-700">Nombre del método</label>
              <div className="flex items-center gap-2">
                <input 
                  type="text" 
                  id="nombreMetodo" 
                  value={nombreMetodo}
                  onChange={(e) => setNombreMetodo(e.target.value)}
                  className="p-2 border border-gray-300 rounded-md text-sm max-w-[200px] focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  required
                />
                <HelpCircle size={16} className="text-blue-500 cursor-help" title="Nombre descriptivo para el método de cobro" />
              </div>
            </div>

            <div className="grid grid-cols-[200px_1fr] gap-4 items-center mb-4">
              <label htmlFor="numeroCuenta" className="font-medium text-right text-gray-700">Número de tarjeta / Cuenta corriente</label>
              <div className="flex items-center gap-2">
                <input 
                  type="text" 
                  id="numeroCuenta" 
                  value={numeroCuenta}
                  onChange={(e) => setNumeroCuenta(e.target.value)}
                  className="p-2 border border-gray-300 rounded-md text-sm max-w-[200px] focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
                <HelpCircle size={16} className="text-blue-500 cursor-help" title="Últimos 4 dígitos de la tarjeta o cuenta" />
              </div>
            </div>

            <div className="grid grid-cols-[200px_1fr] gap-4 items-center mb-4">
              <label htmlFor="cuentaVinculada" className="font-medium text-right text-gray-700">Cuenta vinculada</label>
              <select 
                id="cuentaVinculada" 
                value={cuentaVinculada}
                onChange={(e) => setCuentaVinculada(e.target.value)}
                className="p-2 border border-gray-300 rounded-md text-sm bg-white max-w-[200px] focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              >
                <option>Nueva cuenta</option>
                {/* Opciones de cuentas existentes */}
              </select>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mt-6 text-sm text-blue-800">
              <h3 className="font-semibold mb-2 text-blue-700">Instrucciones</h3>
              <ul className="list-disc ml-5 mb-4 text-blue-800">
                <li>Para poder hacer un seguimiento correcto de los pagos y cobros es necesario indicar el medio de pago y el número de cuenta o tarjeta utilizado en cada pago o cobro concreto.</li>
                <li>Cogid Contasimple no hace ningún uso de dicha información, que se guarda a salvo en nuestra base de datos.</li>
                <li>Si lo prefieres, puedes registrar el número de cuenta o tarjeta indicando únicamente los últimos cuatro dígitos.</li>
              </ul>
              <div className="font-semibold italic mb-2 text-blue-700">Ejemplo:</div>
              <table className="w-full border-collapse text-xs">
                <thead>
                  <tr>
                    <th className="border border-blue-300 p-2 text-center bg-blue-100 font-semibold">Nombre</th>
                    <th className="border border-blue-300 p-2 text-center bg-blue-100 font-semibold">Tipo Método</th>
                    <th className="border border-blue-300 p-2 text-center bg-blue-100 font-semibold">Número de tarjeta o cuenta</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-blue-300 p-2 text-center bg-blue-50">Cuenta Empresa</td>
                    <td className="border border-blue-300 p-2 text-center bg-blue-50">Transferencia</td>
                    <td className="border border-blue-300 p-2 text-center bg-blue-50">8090</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </form>
        </div>

        {/* Modal Footer */}
        <div className="p-4 border-t border-gray-200 flex justify-end gap-3 bg-gray-50">
          <button 
            type="submit"
            onClick={handleSubmit}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            disabled={loading}
          >
            {loading ? 'Guardando...' : 'Insertar'}
          </button>
          <button 
            type="button" 
            onClick={onClose} 
            className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-2 px-4 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            disabled={loading}
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModalMetodosCobro;
